import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IProject } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

import { CandidateProjectFormSchema } from "@/components/forms/candidate/add-update-project/schema";

export const useAddUpdateProject = (
  profileId: string,
  onSuccess?: (v: IProject) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      data,
      projectId,
    }: {
      data: CandidateProjectFormSchema;
      projectId?: string;
    }) => {
      const token = await getAccessTokenSilently();
      const body = buildBody(data);

      return request<IProject>({
        method: projectId ? "PATCH" : "POST",
        path: `/api/candidate/${profileId || "foo"}/project/${projectId ?? ""}`,
        token,
        body,
        contentType: "application/x-www-form-urlencoded",
      });
    },
    onSuccess: (data, { projectId }) => {
      // Update project for candidate
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };
        if (projectId) {
          const projects = [...prevCandidate.projects];
          const prevProjectIdx = projects.findIndex(
            ({ id }) => id === projectId,
          );

          if (prevProjectIdx !== -1) {
            projects[prevProjectIdx] = data;
          }
          updatedCandidate.projects = projects;
        } else {
          updatedCandidate.projects = [...prevCandidate.projects, data];
        }

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      // Update project
      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };
          if (projectId) {
            const projects = [...prevSelf.projects];
            const prevProjectIdx = projects.findIndex(
              ({ id }) => id === projectId,
            );

            if (prevProjectIdx !== -1) {
              projects[prevProjectIdx] = data;
            }
            updatedCandidate.projects = projects;
          } else {
            updatedCandidate.projects = [...prevSelf.projects, data];
          }

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.(data);
    },
    onError: () => {
      toast.error("Failed to save project, please try again later.");
    },
  });

  return {
    updateProject: (payload: {
      data: CandidateProjectFormSchema;
      projectId: string;
    }) => mutate(payload),
    addProject: (payload: { data: CandidateProjectFormSchema }) =>
      mutate(payload),
    isUpdatingProject: isPending,
    updateProjectError: error,
  };
};

function buildBody(data: CandidateProjectFormSchema) {
  const body = new FormData();
  body.append("title", data.title);
  body.append("summary", data.summary);

  if (data.image) {
    body.append("image", data.image);
  }

  if (data.project) {
    body.append("project", data.project);
  } else if (data.link) {
    body.append("link", data.link);
  }

  return body;
}
